import { useCallback, useEffect, useState } from 'react';
import { boardApi } from '@/features/project-board/api';
import { useBoard } from '@/features/project-board/BoardProvider';
import { IndexSettingsTable, ReindexFailure, type ReindexNote } from '@/features/project-board/reindex';
import type { IndexSettings, ReindexScope } from '@/features/project-board/types';
import { KstDateTime } from '@/shared/datetime';

const TABLE = 'w-full table-fixed border-collapse text-left text-[12px]';
const HEAD = 'border-b border-line bg-line-soft text-[11px] text-muted [&>th]:px-3 [&>th]:py-2 [&>th]:font-medium';
const ROW = 'border-b border-line last:border-0 [&>td]:px-3 [&>td]:py-2.5 [&>td]:align-middle';
const BUTTON =
  'cursor-pointer rounded-md border border-accent bg-accent px-2.5 py-1 text-[11px] text-white whitespace-nowrap transition hover:brightness-95 active:brightness-90 disabled:cursor-not-allowed disabled:opacity-40';

/** 값이 아직 없는 칸 — 빈 칸으로 두면 표가 깨져 보인다. */
const BLANK = '—';

/**
 * 색인 설정.
 *
 * 보드는 색인의 사영이라, 문서가 그대로인데 판정만 바뀐 경우에는 아무 실행도 그것을 다시 읽지 않는다.
 * 그때 사람이 직접 다시 읽게 하는 곳이 여기다. 버튼은 색인을 **지금 돌리지 않는다** — 다음 배치가
 * 무엇을 다시 읽을지 남길 뿐이라, 언제 반영되는지를 함께 보여 준다.
 *
 * 줄 하나가 **저장소 하나 × 범위 하나**다 — 재색인이 저장소를 가리므로 "fo의 이슈만 다시 읽기"가
 * 각각의 줄이 된다. 마지막 색인·읽은 커밋은 저장소당 하나뿐이라 그 저장소의 첫 줄에 rowSpan으로
 * 한 번만 싣는다. 줄을 만드는 일은 `IndexSettingsTable.rows`가 하고 여기는 그리기만 한다.
 */
export function IndexSettingsView(): React.JSX.Element {
  const { sources } = useBoard();
  const [settings, setSettings] = useState<IndexSettings | null>(null);
  /** 요청 중인 (저장소, 범위) — 같은 줄의 버튼만 "요청 중"으로 바뀌어야 한다. */
  const [pending, setPending] = useState<string | null>(null);
  const [note, setNote] = useState<ReindexNote | null>(null);

  const load = useCallback(() => {
    boardApi
      .settings()
      .then(setSettings)
      .catch((error: unknown) => setNote(ReindexFailure.noteOf(error)));
  }, []);

  useEffect(load, [load]);

  const request = async (source: string, scope: ReindexScope, label: string): Promise<void> => {
    setPending(keyOf(source, scope));
    setNote(null);
    try {
      await boardApi.requestReindex(scope, source);
      setNote({ tone: 'ok', text: `${source} · ${label} 재색인 예약 — 다음 색인에 반영된다` });
      load();
    } catch (error) {
      setNote(ReindexFailure.noteOf(error));
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-bold">프로젝트 보드 설정</h1>
        <p className="text-[12px] text-muted">하네스 저장소별 색인 상태와 재색인 예약을 관리합니다.</p>
      </header>
      <section className="rounded-lg border border-line bg-surface">
        <header className="flex items-center gap-3 border-b border-line p-4">
          <div>
            <h2 className="text-[13px] font-bold">
              색인
              <span className="ml-2 text-[11px] font-normal text-muted">배치 주기 {settings?.schedule ?? '불러오는 중…'}</span>
            </h2>
            <p className="text-[11px] text-faint">
              재색인은 문서가 그대로여도 다시 읽는다. 버튼은 즉시 실행이 아닌 다음 배치의 범위를 예약한다.
              시각은 KST이며, 변경이 없어 건너뛴 실행은 마지막 색인 시각을 바꾸지 않는다.
            </p>
          </div>
        </header>
        {note !== null && (
          <p className={`border-b border-line bg-line-soft px-4 py-2 text-[12px] ${note.tone === 'ok' ? 'text-muted' : 'text-rose-700'}`}>
            {note.text}
          </p>
        )}
        <div className="overflow-x-auto">
          <table className={TABLE}>
          <colgroup>
            <col className="w-[104px]" />
            <col className="w-[104px]" />
            <col />
            <col className="w-[128px]" />
            <col className="w-[84px]" />
            <col className="w-[132px]" />
            <col className="w-[104px]" />
          </colgroup>
          <thead>
            <tr className={HEAD}>
              <th>하네스 저장소</th>
              <th>범위</th>
              <th>다시 읽는 것</th>
              <th>예약</th>
              <th className="text-right">요청</th>
              <th>마지막 색인</th>
              <th>읽은 커밋</th>
            </tr>
          </thead>
          <tbody>
            {IndexSettingsTable.rows(settings?.pending ?? [], sources).map((row) => (
              <tr key={keyOf(row.source.key, row.target.scope)} className={ROW}>
                {/* 저장소 칸은 그 저장소의 첫 줄에만 — 범위 수만큼 같은 값을 반복하지 않는다. */}
                {row.opensSource && (
                  <td
                    rowSpan={row.sourceRowSpan}
                    className="border-r border-line-soft align-top font-mono text-[12px]"
                  >
                    {row.source.key}
                  </td>
                )}
                <td className="text-[13px] font-semibold">{row.target.label}</td>
                <td className="text-xs leading-snug text-muted">{row.target.hint}</td>
                <td className="text-[11.5px]">
                  <Reservation reservedAt={row.reservedAt} />
                </td>
                <td className="text-right">
                  <button
                    onClick={() => void request(row.source.key, row.target.scope, row.target.label)}
                    disabled={pending !== null}
                    className={BUTTON}
                  >
                    {pending === keyOf(row.source.key, row.target.scope) ? '요청 중…' : '재색인'}
                  </button>
                </td>
                {row.opensSource && (
                  <td rowSpan={row.sourceRowSpan} className="align-top text-[12.5px] whitespace-nowrap">
                    {KstDateTime.format(row.source.scannedAt) || BLANK}
                  </td>
                )}
                {/* 화면이 지금 어느 시점 문서를 보고 있는지 — 앞 7자면 커밋을 특정하기에 충분하다. */}
                {row.opensSource && (
                  <td rowSpan={row.sourceRowSpan} className="align-top font-mono text-[11.5px] text-faint">
                    {row.source.commitSha?.slice(0, 7) ?? BLANK}
                  </td>
                )}
              </tr>
            ))}
            {sources.length === 0 && (
              <tr>
                <td className="p-6 text-center text-xs text-muted" colSpan={7}>
                  아직 색인된 하네스 저장소가 없다.
                </td>
              </tr>
            )}
          </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

/** 그 범위가 다음 색인을 기다리는지. 예약은 한 번에 하나라 최대 한 줄만 켜진다. */
function Reservation({ reservedAt }: { reservedAt: string | null }): React.JSX.Element {
  if (reservedAt === null) return <span className="text-faint">{BLANK}</span>;

  return (
    <>
      <div className="text-accent">다음 색인 대기 중</div>
      <div className="text-[11px] text-muted">{KstDateTime.format(reservedAt)} 요청</div>
    </>
  );
}

/** 줄 하나를 가리키는 키 — 어느 버튼이 눌렸는지, 어느 줄을 그리는지가 같은 값으로 갈린다. */
function keyOf(source: string, scope: ReindexScope): string {
  return `${source}:${scope}`;
}
